/**
 * VFXEmitterManager - Core emitter management
 * 
 * Manages VFX emitters without editor/Vue dependencies.
 * Handles:
 * - Emitter lifecycle (create, update, destroy)
 * - Graph compilation caching
 * - Spawn rate and burst logic
 * - Position updates
 * 
 * Usage:
 * ```typescript
 * const manager = new VFXEmitterManager(renderContext, {
 *   graphLoader: async (path) => await vfs.readFile(path).content,
 * });
 * 
 * const emitterId = await manager.createEmitter('/vfx/explosion.vfx', { x: 100, y: 200 });
 * manager.update(deltaTime);
 * manager.render(viewProjectionMatrix);
 * manager.destroyEmitter(emitterId);
 * ```
 */

import type { IRenderContext, TextureHandle } from '@engine/render2d';
import { VFXMultiSystemRenderer } from '../graph/VFXMultiSystemRenderer';
import { loadTextureFromURL } from '../graph/textureUtils';
import { VFXCompiler } from '../graph/compiler/VFXCompiler';
import { resolveVariableSlot } from '../graph/compiler/codegen/variableSlots';
import { deserializeVFXGraph } from '../graph/VFXGraphAsset';
import type { CompiledMultiSystemData } from '../graph/VFXMultiSystemRenderer';
import type { CompiledSystemData } from '../graph/VFXSystemRenderer';
import type { VFXGraphData } from '../graph/types';
import type {
  EmitterState,
  EmitterOptions,
  EmitterBurstConfig,
  GraphLoader,
  IVFXRenderer,
  EmitterManagerOptions,
  EmitterManagerStats,
  TextureResolver,
  PointmapResolver,
  CompiledDataLoader,
  ShaderLoader,
  LightEmittingSystemInfo,
} from './types';


// ============================================================================
// TYPES
// ============================================================================

interface ManagedEmitter extends EmitterState {
  renderer: IVFXRenderer | null;
}

/**
 * Cached graph data for texture variable extraction
 */
interface CachedGraphData {
  variables: VFXGraphData['variables'];
}

// ============================================================================
// MANAGER
// ============================================================================

/**
 * Core VFX emitter management system.
 * 
 * Platform-agnostic - works with any render context that supports compute shaders.
 */
export class VFXEmitterManager {
  private emitters: Map<string, ManagedEmitter> = new Map();
  private compiledCache: Map<string, CompiledMultiSystemData> = new Map();
  private graphDataCache: Map<string, CachedGraphData> = new Map();
  private renderContext: IRenderContext;
  private graphLoader: GraphLoader;
  private compiledDataLoader: CompiledDataLoader | null;
  private shaderLoader: ShaderLoader | null;
  private textureResolver: TextureResolver | null;
  private pointmapResolver: PointmapResolver | null;
  private nextEmitterId = 0;
  private debug: boolean;
  private maxEmitters: number;
  private _defaultTexture: TextureHandle | null = null;
  
  constructor(
    renderContext: IRenderContext,
    options: EmitterManagerOptions & { graphLoader: GraphLoader }
  ) {
    this.renderContext = renderContext;
    this.graphLoader = options.graphLoader;
    this.compiledDataLoader = options.compiledDataLoader ?? null;
    this.shaderLoader = options.shaderLoader ?? null;
    this.textureResolver = options.textureResolver ?? null;
    this.pointmapResolver = options.pointmapResolver ?? null;
    this.debug = options.debug ?? false;
    this.maxEmitters = options.maxEmitters ?? 1000;
    
    if (!renderContext.supportsCompute) {
      console.warn('[VFXEmitterManager] Render context does not support compute shaders');
    }
  }
  
  // ==========================================================================
  // DEFAULT TEXTURE
  // ==========================================================================
  
  /**
   * Set the default fallback texture used when no texture is bound.
   * This texture is applied to all newly created emitters.
   * @param texture - Texture handle to use as default
   */
  setDefaultTexture(texture: TextureHandle): void {
    this._defaultTexture = texture;
    
    // Apply to existing emitters
    for (const emitter of this.emitters.values()) {
      if (emitter.renderer && 'setDefaultTexture' in emitter.renderer) {
        (emitter.renderer as { setDefaultTexture: (t: TextureHandle) => void }).setDefaultTexture(texture);
      }
    }
  }
  
  /**
   * Load a custom default texture from URL.
   * @param url - URL to the texture image
   * @param label - Optional label for debugging
   */
  async loadDefaultTextureFromURL(url: string, label?: string): Promise<void> {
    const texture = await loadTextureFromURL(this.renderContext, url, { label });
    this.setDefaultTexture(texture);
  }
  
  /**
   * Get the current default texture.
   */
  getDefaultTexture(): TextureHandle | null {
    return this._defaultTexture;
  }
  
  // ==========================================================================
  // EMITTER LIFECYCLE
  // ==========================================================================
  
  /**
   * Create a new emitter from a VFX asset path.
   * 
   * @param assetPath - Path to VFX graph asset
   * @param options - Emitter options
   * @returns Emitter ID
   */
  async createEmitter(assetPath: string, options: EmitterOptions = {}): Promise<string> {
    if (this.emitters.size >= this.maxEmitters) {
      throw new Error(`[VFXEmitterManager] Maximum emitters (${this.maxEmitters}) reached`);
    }
    
    const id = `emitter_${this.nextEmitterId++}`;
    
    const emitter: ManagedEmitter = {
      id,
      assetPath,
      x: options.x ?? 0,
      y: options.y ?? 0,
      scaleX: 1,
      scaleY: 1,
      playing: options.playOnStart ?? true,
      loop: options.loop ?? true,
      playbackSpeed: options.playbackSpeed ?? 1,
      spawnRate: 0,
      spawnAccumulator: 0,
      burst: null,
      time: 0,
      renderer: null,
    };
    
    this.emitters.set(id, emitter);
    
    // Load and compile graph
    try {
      const compiled = await this.loadAndCompileGraph(assetPath);
      
      if (compiled) {
        this.initEmitter(emitter, compiled);
      } else {
        console.error(`[VFXEmitterManager] Failed to compile graph: ${assetPath}`);
      }
      
      // Bind texture variables after renderer is created
      if (emitter.renderer && this.textureResolver) {
        await this.bindTextureVariables(emitter, assetPath);
      }
    } catch (err) {
      console.error(`[VFXEmitterManager] Error loading graph ${assetPath}:`, err);
    }
    
    return id;
  }
  
  /**
   * Destroy an emitter and release its GPU resources.
   */
  destroyEmitter(id: string): boolean {
    const emitter = this.emitters.get(id);
    if (!emitter) return false;
    
    if (emitter.renderer) {
      emitter.renderer.destroy();
    }
    
    this.emitters.delete(id);
    return true;
  }
  
  /**
   * Destroy all emitters.
   */
  destroyAll(): void {
    for (const [id, emitter] of this.emitters) {
      if (emitter.renderer) {
        emitter.renderer.destroy();
      }
    }
    this.emitters.clear();
  }
  
  // ==========================================================================
  // EMITTER CONTROL
  // ==========================================================================
  
  /**
   * Set emitter position.
   */
  setPosition(id: string, x: number, y: number): void {
    const emitter = this.emitters.get(id);
    if (emitter) {
      emitter.x = x;
      emitter.y = y;
    }
  }
  
  /**
   * Set emitter scale.
   */
  setScale(id: string, x: number, y: number): void {
    const emitter = this.emitters.get(id);
    if (emitter) {
      emitter.scaleX = x;
      emitter.scaleY = y;
    }
  }
  
  /**
   * Start playing an emitter.
   */
  play(id: string): void {
    const emitter = this.emitters.get(id);
    if (emitter) {
      emitter.playing = true;
    }
  }
  
  /**
   * Stop playing an emitter.
   */
  stop(id: string): void {
    const emitter = this.emitters.get(id);
    if (emitter) {
      emitter.playing = false;
    }
  }
  
  /**
   * Manually spawn particles.
   */
  spawn(id: string, count: number): void {
    const emitter = this.emitters.get(id);
    if (emitter?.renderer) {
      emitter.renderer.spawn(count);
    }
  }
  
  /**
   * Set loop mode for an emitter.
   */
  setLoop(id: string, loop: boolean): void {
    const emitter = this.emitters.get(id);
    if (emitter) {
      emitter.loop = loop;
      emitter.renderer?.setLoop?.(loop);
    }
  }
  
  /**
   * Check if an emitter's effect is fully finished (spawns exhausted + particles dead).
   */
  isEmitterComplete(id: string): boolean {
    const emitter = this.emitters.get(id);
    if (!emitter?.renderer) return false;
    return emitter.renderer.isComplete ?? false;
  }
  
  /**
   * Set a texture for a variable slot on an emitter.
   * Use this to bind textures to texture variables exposed in the VFX graph.
   * @param id - Emitter ID
   * @param slot - Texture slot index (0-7)
   * @param texture - Texture handle (or null to clear)
   */
  setTexture(id: string, slot: number, texture: TextureHandle | null): void {
    const emitter = this.emitters.get(id);
    if (emitter?.renderer && 'setTexture' in emitter.renderer) {
      (emitter.renderer as { setTexture: (slot: number, texture: TextureHandle | null) => void }).setTexture(slot, texture);
    }
  }
  
  /**
   * Set a float variable on an emitter.
   * @param id - Emitter ID
   * @param index - Variable index (0-15)
   * @param value - Float value
   */
  setFloat(id: string, index: number, value: number): void {
    const emitter = this.emitters.get(id);
    if (emitter?.renderer && 'setFloat' in emitter.renderer) {
      (emitter.renderer as { setFloat: (index: number, value: number) => void }).setFloat(index, value);
    }
  }
  
  /**
   * Set a vec2 variable on an emitter.
   * @param id - Emitter ID
   * @param index - Variable index (0-7)
   * @param x - X component
   * @param y - Y component
   */
  setVec2(id: string, index: number, x: number, y: number): void {
    const emitter = this.emitters.get(id);
    if (emitter?.renderer && 'setVec2' in emitter.renderer) {
      (emitter.renderer as { setVec2: (index: number, x: number, y: number) => void }).setVec2(index, x, y);
    }
  }
  
  /**
   * Set a vec3 variable on an emitter.
   * @param id - Emitter ID
   * @param index - Variable index (0-7)
   * @param x - X component
   * @param y - Y component
   * @param z - Z component
   */
  setVec3(id: string, index: number, x: number, y: number, z: number): void {
    const emitter = this.emitters.get(id);
    if (emitter?.renderer && 'setVec3' in emitter.renderer) {
      (emitter.renderer as { setVec3: (index: number, x: number, y: number, z: number) => void }).setVec3(index, x, y, z);
    }
  }
  
  /**
   * Set a color variable on an emitter.
   * @param id - Emitter ID
   * @param index - Variable index (0-7)
   * @param r - Red component (0-1)
   * @param g - Green component (0-1)
   * @param b - Blue component (0-1)
   * @param a - Alpha component (0-1, default 1)
   */
  setColor(id: string, index: number, r: number, g: number, b: number, a: number = 1): void {
    const emitter = this.emitters.get(id);
    if (emitter?.renderer && 'setColor' in emitter.renderer) {
      (emitter.renderer as { setColor: (index: number, r: number, g: number, b: number, a: number) => void }).setColor(index, r, g, b, a);
    }
  }

  /**
   * Set a graph variable on an emitter by NAME (or id). Resolves the packed
   * slot through the shared resolver (`resolveVariableSlot`) — the same
   * convention the shader codegen and component overrides use.
   *
   * Value shapes: float/int → number; vec2 → {x,y}; vec3 → {x,y,z};
   * color/vec4 → {r,g,b,a}. Texture variables are not settable here (they
   * need async GUID resolution — use the host subsystem's texture path).
   * @returns true when the variable resolved and was written.
   */
  setVariable(id: string, ref: string, value: unknown): boolean {
    const emitter = this.emitters.get(id);
    if (!emitter) return false;
    const variables = this.graphDataCache.get(emitter.assetPath)?.variables;
    if (!variables) return false;
    const resolved = resolveVariableSlot(variables, ref);
    if (!resolved) return false;

    const num = (v: unknown, fallback = 0) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback;
    };
    const obj = (value ?? {}) as Record<string, unknown>;

    switch (resolved.category) {
      case 'float':
        this.setFloat(id, resolved.slot, num(value));
        return true;
      case 'vec2':
        this.setVec2(id, resolved.slot, num(obj.x), num(obj.y));
        return true;
      case 'vec3':
        this.setVec3(id, resolved.slot, num(obj.x), num(obj.y), num(obj.z));
        return true;
      case 'color':
        this.setColor(id, resolved.slot, num(obj.r ?? obj.x, 1), num(obj.g ?? obj.y, 1), num(obj.b ?? obj.z, 1), num(obj.a ?? obj.w, 1));
        return true;
      default:
        return false;
    }
  }

  /**
   * Get emitter state.
   */
  getEmitter(id: string): EmitterState | null {
    const emitter = this.emitters.get(id);
    if (!emitter) return null;
    
    // Return copy without internal renderer
    const { renderer, ...state } = emitter;
    return state;
  }
  
  /**
   * Get graph variables for a VFX asset path.
   * Used to apply variable overrides after emitter creation.
   * @param assetPath - VFX asset path or emitter ID
   */
  getGraphVariables(assetPathOrId: string): VFXGraphData['variables'] | null {
    // Try as emitter ID first
    const emitter = this.emitters.get(assetPathOrId);
    if (emitter) {
      const cached = this.graphDataCache.get(emitter.assetPath ?? '');
      if (cached) return cached.variables;
    }
    
    // Try as direct path
    const cached = this.graphDataCache.get(assetPathOrId);
    return cached ? cached.variables : null;
  }
  
  // ==========================================================================
  // UPDATE & RENDER
  // ==========================================================================
  
  /**
   * Update all active emitters.
   * 
   * @param deltaTime - Time since last frame in seconds
   */
  update(deltaTime: number): void {
    for (const [id, emitter] of this.emitters) {
      if (!emitter.playing || !emitter.renderer) {
        continue;
      }
      
      const dt = Math.min(deltaTime, 0.1) * emitter.playbackSpeed;
      emitter.time += dt;
      
      // Update renderer position
      emitter.renderer.setPosition(emitter.x, emitter.y);
      emitter.renderer.setScale(emitter.scaleX, emitter.scaleY);
      
      // Handle burst spawning
      if (emitter.burst && emitter.burst.remaining > 0) {
        emitter.burst.timer -= dt;
        if (emitter.burst.timer <= 0) {
          emitter.renderer.spawn(emitter.burst.count);
          emitter.burst.remaining--;
          emitter.burst.timer = emitter.burst.interval;
          
        }
      }
      
      // Handle rate-based spawning
      if (emitter.spawnRate > 0) {
        emitter.spawnAccumulator += emitter.spawnRate * dt;
        if (emitter.spawnAccumulator >= 1) {
          const toSpawn = Math.floor(emitter.spawnAccumulator);
          emitter.spawnAccumulator -= toSpawn;
          emitter.renderer.spawn(toSpawn);
        }
      }
      
      // Update particle simulation
      emitter.renderer.update(dt);
    }
  }
  
  /**
   * Render all active emitters.
   * 
   * @param viewProjection - View-projection matrix (Float32Array of length 16)
   * @param ctx - Optional render context (uses stored ctx if not provided)
   * @param isHdrTarget - Whether rendering to an HDR target (rgba16float). Default false for screen.
   */
  render(viewProjection: Float32Array, ctx?: IRenderContext, isHdrTarget: boolean = false): void {
    for (const [id, emitter] of this.emitters) {
      if (!emitter.playing || !emitter.renderer) continue;
      emitter.renderer.render(viewProjection, ctx, isHdrTarget);
    }
  }
  
  /**
   * Collect all light-emitting VFX systems across all active emitters.
   * Returns system info needed by VFXLightInjectPass.
   */
  getLightEmittingSystems(): LightEmittingSystemInfo[] {
    const result: LightEmittingSystemInfo[] = [];
    for (const emitter of this.emitters.values()) {
      if (!emitter.playing || !emitter.renderer) continue;
      if (!emitter.renderer.getLightEmittingSystems) continue;
      const systems = emitter.renderer.getLightEmittingSystems();
      for (const sys of systems) {
        result.push(sys);
      }
    }
    return result;
  }
  
  // ==========================================================================
  // STATISTICS
  // ==========================================================================
  
  /**
   * Get manager statistics.
   */
  getStats(): EmitterManagerStats {
    let totalParticles = 0;
    
    for (const emitter of this.emitters.values()) {
      if (emitter.renderer?.getAliveCount) {
        totalParticles += emitter.renderer.getAliveCount();
      }
    }
    
    return {
      emitterCount: this.emitters.size,
      totalParticles,
      cacheSize: this.compiledCache.size,
    };
  }
  
  /**
   * Get number of active emitters.
   */
  get emitterCount(): number {
    return this.emitters.size;
  }
  
  // ==========================================================================
  // CACHE MANAGEMENT
  // ==========================================================================
  
  /**
   * Clear compiled graph cache.
   */
  clearCache(): void {
    this.compiledCache.clear();
  }
  
  // ==========================================================================
  // GRAPH COMPILATION
  // ==========================================================================
  
  private async loadAndCompileGraph(path: string): Promise<CompiledMultiSystemData | null> {
    // Check cache
    if (this.compiledCache.has(path)) {
      return this.compiledCache.get(path)!;
    }
    
    // Try loading pre-compiled data first (from AID cache)
    if (this.compiledDataLoader) {
      try {
        const precompiled = await this.compiledDataLoader(path);
        if (precompiled && precompiled.systems.length > 0) {
          return await this.usePrecompiledData(path, precompiled);
        }
      } catch (err) {
        // Fall back to compiling from source
      }
    }
    
    // Load graph content and compile
    const content = await this.graphLoader(path);
    if (!content) {
      console.error(`[VFXEmitterManager] Failed to load graph: ${path}`);
      return null;
    }
    
    try {
      const graphData = JSON.parse(content);
      return this.compileGraph(path, graphData as VFXGraphData, content);
    } catch (err) {
      console.error(`[VFXEmitterManager] Error compiling ${path}:`, err);
      return null;
    }
  }
  
  /**
   * Use pre-compiled data from cache (skips compilation)
   * If shaderLoader is available and system has shaderPaths, loads shaders from files.
   */
  private async usePrecompiledData(path: string, precompiled: import('./types').CompiledDataResult): Promise<CompiledMultiSystemData> {
    const systems = precompiled.systems;
    
    // Process systems - potentially load shaders from files
    const processedSystems = await Promise.all(systems.map(async sys => {
      let initShader = sys.initShader;
      let updateShader = sys.updateShader;
      let vertexShader = sys.vertexShader;
      let fragmentShader = sys.fragmentShader;
      
      // If shaderLoader is available and system has shaderPaths, try loading from files
      if (this.shaderLoader && sys.shaderPaths) {
        try {
          const [init, update, vertex, fragment] = await Promise.all([
            this.shaderLoader(sys.shaderPaths.initShader),
            this.shaderLoader(sys.shaderPaths.updateShader),
            this.shaderLoader(sys.shaderPaths.vertexShader),
            this.shaderLoader(sys.shaderPaths.fragmentShader),
          ]);
          
          // Use loaded shaders if successful, otherwise fall back to inline
          if (init) initShader = init;
          if (update) updateShader = update;
          if (vertex) vertexShader = vertex;
          if (fragment) fragmentShader = fragment;
          
        } catch (err) {
          // Fall back to inline shaders
        }
      }
      
      return {
        id: sys.id,
        name: sys.name,
        initShader,
        updateShader,
        vertexShader,
        fragmentShader,
        maxParticles: sys.maxParticles,
        spawnMode: 'direct' as const,
        spawnType: (sys.spawnType ?? (sys.spawnRate ? 'rate' : sys.burst ? 'burst' : undefined)) as CompiledSystemData['spawnType'],
        spawnRate: sys.spawnRate ?? 0,
        variableRate: sys.variableRate,
        burst: sys.burst,
        usesTexture: sys.usesTexture,
        textureSlot: sys.textureSlot,
        emitsLight: sys.emitsLight,
        lightRenderQuad: sys.lightRenderQuad,
        lightConfig: sys.lightConfig ? { ...sys.lightConfig } : undefined,
        isLit: sys.isLit,
      };
    }));
    
    const compiled: CompiledMultiSystemData = {
      name: path.split('/').pop() ?? 'VFX',
      systems: processedSystems,
      particleStruct: precompiled.particleStruct,
    };
    
    this.compiledCache.set(path, compiled);
    return compiled;
  }
  
  private async compileGraph(
    path: string,
    graphData: VFXGraphData,
    content: string,
    options?: { tweakableInputs?: boolean }
  ): Promise<CompiledMultiSystemData | null> {
    // Runtime fallback for graphs without a precompiled bundle. Requires an
    // injected Slang compiler (editor); the player always ships precompiled
    // shaders and never takes this path.
    const graph = deserializeVFXGraph(content);
    const compiler = new VFXCompiler({ debug: this.debug, tweakableInputs: options?.tweakableInputs });
    const result = await compiler.compile(graph);

    if (!result.success || result.systems.length === 0) {
      return null;
    }

    const compiled: CompiledMultiSystemData = {
      name: graphData.name,
      systems: result.systems.map(sys => ({
        id: sys.id,
        name: sys.name,
        initShader: sys.initShader,
        updateShader: sys.updateShader,
        vertexShader: sys.vertexShader,
        fragmentShader: sys.fragmentShader,
        maxParticles: sys.maxParticles,
        spawnMode: sys.spawn.mode === 'event' ? 'onEvent' as const : 'direct' as const,
        spawnType: sys.spawn.mode === 'event' || sys.spawn.mode === 'none' 
          ? undefined 
          : sys.spawn.mode as 'rate' | 'variable_rate' | 'single_burst' | 'periodic_burst' | 'burst',
        spawnRate: sys.spawn.rate ?? 0,
        variableRate: sys.spawn.variableRate,
        burst: sys.spawn.burst ? {
          count: sys.spawn.burst.count,
          delay: sys.spawn.burst.delay,
          repeat: sys.spawn.burst.repeat,
          interval: sys.spawn.burst.interval,
        } : undefined,
        inputEventId: sys.inputEvent?.eventId,
        outputEventId: sys.outputEvent?.eventId,
        spawnPerEvent: sys.inputEvent?.spawnCount ?? sys.outputEvent?.spawnCount ?? 50,
        usesTexture: sys.usesTexture,
        textureSlot: sys.textureSlot,
        emitsLight: sys.emitsLight,
        lightRenderQuad: sys.lightRenderQuad,
        lightConfig: sys.lightConfig ? { ...sys.lightConfig } : undefined,
        isLit: sys.isLit,
        tweaks: sys.tweaks,
      })),
      particleStruct: result.particleStruct,
    };

    this.compiledCache.set(path, compiled);
    this.graphDataCache.set(path, { variables: graphData.variables ?? [] });
    return compiled;
  }

  // ==========================================================================
  // EDITOR LIVE PREVIEW
  // ==========================================================================

  /**
   * Editor live-preview: compile the (possibly unsaved) graph content in tweak
   * mode and hot-swap the renderers of every live emitter using this asset.
   * Tweak-mode systems route unconnected float-family inputs through uniform
   * slots, so subsequent {@link setTweakValue} calls update them without any
   * recompilation.
   *
   * NOTE: an emitter created for this asset AFTER the push reloads the
   * imported (literal-baked) data — push again to cover it.
   */
  async applyPreview(assetPath: string, content: string): Promise<boolean> {
    let compiled: CompiledMultiSystemData | null = null;
    try {
      const raw = JSON.parse(content) as { graph?: VFXGraphData } & VFXGraphData;
      const graphData = (raw.graph ?? raw) as VFXGraphData;
      compiled = await this.compileGraph(assetPath, graphData, content, { tweakableInputs: true });
    } catch (err) {
      console.error(`[VFXEmitterManager] Preview compile failed for ${assetPath}:`, err);
      return false;
    }
    if (!compiled) return false;

    let swapped = false;
    for (const emitter of this.emitters.values()) {
      if (emitter.assetPath !== assetPath) continue;
      emitter.renderer?.destroy();
      emitter.renderer = null;
      this.initEmitter(emitter, compiled);
      await this.bindTextureVariables(emitter, assetPath);
      swapped = true;
    }
    return swapped;
  }

  /**
   * Editor live-preview: write a tweak value for a graph port on every live
   * emitter of the asset. Returns true if any renderer had a binding (i.e.
   * the value is live — no recompile needed).
   */
  setTweakValue(assetPath: string, nodeId: string, portId: string, value: unknown): boolean {
    let applied = false;
    for (const emitter of this.emitters.values()) {
      if (emitter.assetPath !== assetPath) continue;
      const renderer = emitter.renderer;
      if (renderer && renderer instanceof VFXMultiSystemRenderer) {
        applied = renderer.setTweakValue(nodeId, portId, value) || applied;
      }
    }
    return applied;
  }
  
  // ==========================================================================
  // EMITTER INITIALIZATION
  // ==========================================================================
  
  private initEmitter(emitter: ManagedEmitter, compiled: CompiledMultiSystemData): void {
    const renderer = new VFXMultiSystemRenderer(this.renderContext, compiled, {
      label: `vfx-${emitter.id}`,
      defaultTexture: this._defaultTexture ?? undefined,
    });
    
    emitter.renderer = renderer;
    
    // Pass loop flag to renderer so it can auto-reset finite spawn systems
    renderer.setLoop(emitter.loop);
    
    // VFXMultiSystemRenderer now handles spawn rates and bursts internally
    // for ALL systems, so we don't need to track them here anymore.
    // External spawn() calls still work for manual triggering.
    emitter.spawnRate = 0;  // Disable external spawn rate handling
    emitter.burst = null;  // Disable external burst handling
    
    // Set initial position
    renderer.setPosition(emitter.x, emitter.y);
    
  }
  
  // ==========================================================================
  // TEXTURE BINDING
  // ==========================================================================
  
  /**
   * Bind texture variables to emitter.
   * Resolves texture GUIDs and binds them to renderer texture slots.
   */
  private async bindTextureVariables(emitter: ManagedEmitter, assetPath: string): Promise<void> {
    if (!emitter.renderer) return;
    
    const cachedData = this.graphDataCache.get(assetPath);
    if (!cachedData) return;
    
    // Find texture and pointmap variables
    let textureSlot = 0;
    const textureVars = cachedData.variables
      .filter(v => v.type === 'texture2d' || v.type === 'pointmap')
      .slice(0, 8); // Max 8 texture slots
    
    for (const variable of textureVars) {
      const guid = variable.defaultValue as string;
      if (!guid) {
        textureSlot++;
        continue;
      }
      
      try {
        // Use the appropriate resolver based on variable type
        let handle: TextureHandle | null = null;
        
        if (variable.type === 'pointmap') {
          // Use pointmap resolver for pointmap variables
          // Append :color suffix if channel is 'color'
          const resolveGuid = variable.channel === 'color' ? `${guid}:color` : guid;
          
          if (this.pointmapResolver) {
            handle = await this.pointmapResolver(resolveGuid);
          }
        } else {
          // Use texture resolver for regular textures
          if (this.textureResolver) {
            handle = await this.textureResolver(guid);
          }
        }
        
        if (handle !== null && emitter.renderer && 'setTexture' in emitter.renderer) {
          (emitter.renderer as { setTexture: (slot: number, texture: TextureHandle | null) => void })
            .setTexture(textureSlot, handle);
        }
      } catch (err) {
        console.error(`[VFXEmitterManager] Failed to resolve ${variable.type} ${variable.name}:`, err);
      }
      
      textureSlot++;
    }
  }
  
  // ==========================================================================
  // CLEANUP
  // ==========================================================================
  
  /**
   * Destroy the manager and all resources.
   */
  destroy(): void {
    this.destroyAll();
    this.clearCache();
  }
}
